import { useEffect, useRef, useState } from 'react'
import JSZip from 'jszip'

const DATABASE_NAME = 'climatise-site-audit'
const MTC_SLUG = 'the-manufacturing-technology-c-ansty-park-campus-e0c8c0605ce164da84e489034cd6ded9'

interface StoredMediaRecord {
  id: string
  visitId: string
  data?: ArrayBuffer
  blob?: Blob
  mimeType?: string
  uploaded?: boolean
  updatedAt?: string
}

interface RecoveryData {
  capturedAt: string
  databaseVersion: number | null
  visits: Array<Record<string, unknown>>
  operations: Array<Record<string, unknown>>
  conflicts: Array<Record<string, unknown>>
  media: StoredMediaRecord[]
  localStorage: Record<string, string>
}

function readStore(db: IDBDatabase, name: string) {
  return new Promise<unknown[]>((resolve, reject) => {
    if (!db.objectStoreNames.contains(name)) {
      resolve([])
      return
    }
    const request = db.transaction(name, 'readonly').objectStore(name).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function openExistingDatabase() {
  return new Promise<IDBDatabase | null>((resolve, reject) => {
    let upgradeAttempted = false
    const request = window.indexedDB.open(DATABASE_NAME)
    const timer = window.setTimeout(() => reject(new Error('The iPad storage check did not respond within 15 seconds.')), 15_000)
    request.onupgradeneeded = () => {
      upgradeAttempted = true
      request.transaction?.abort()
    }
    request.onsuccess = () => {
      window.clearTimeout(timer)
      resolve(request.result)
    }
    request.onerror = () => {
      window.clearTimeout(timer)
      if (upgradeAttempted && request.error?.name === 'AbortError') resolve(null)
      else reject(request.error ?? new Error('The local audit database could not be opened.'))
    }
  })
}

function safeLocalStorage() {
  const result: Record<string, string> = {}
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (!key || (!key.startsWith('site-audit-') && !key.startsWith('climatise-audit-'))) continue
      result[key] = window.localStorage.getItem(key) ?? ''
    }
  } catch {
    // IndexedDB may still be recoverable when localStorage is restricted.
  }
  return result
}

function withoutBinary(data: RecoveryData) {
  return {
    ...data,
    media: data.media.map(({ data: bytes, blob, ...item }) => ({
      ...item,
      dataBytes: bytes?.byteLength ?? 0,
      blobBytes: blob?.size ?? 0,
      blobType: blob?.type ?? '',
    })),
  }
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export default function RecoveryPage() {
  const [data, setData] = useState<RecoveryData | null>(null)
  const [status, setStatus] = useState('Reading this iPad without changing anything…')
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)
  const databaseRef = useRef<IDBDatabase | null>(null)

  const scan = async () => {
    setError('')
    setStatus('Reading this iPad without changing anything…')
    const localStorage = safeLocalStorage()
    try {
      databaseRef.current?.close()
      const db = await openExistingDatabase()
      databaseRef.current = db
      const [visits, operations, conflicts, media] = db
        ? await Promise.all([
            readStore(db, 'visits'),
            readStore(db, 'operations'),
            readStore(db, 'conflicts'),
            readStore(db, 'media'),
          ])
        : [[], [], [], []]
      const recovered: RecoveryData = {
        capturedAt: new Date().toISOString(),
        databaseVersion: db?.version ?? null,
        visits: visits as Array<Record<string, unknown>>,
        operations: operations as Array<Record<string, unknown>>,
        conflicts: conflicts as Array<Record<string, unknown>>,
        media: media as StoredMediaRecord[],
        localStorage,
      }
      setData(recovered)
      setStatus('Read-only scan complete. Nothing has been synced or changed.')
    } catch (scanError) {
      setData({
        capturedAt: new Date().toISOString(),
        databaseVersion: null,
        visits: [],
        operations: [],
        conflicts: [],
        media: [],
        localStorage,
      })
      setError(scanError instanceof Error ? scanError.message : 'The IndexedDB recovery scan failed.')
      setStatus('Emergency localStorage scan complete. Nothing has been synced or changed.')
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => void scan(), 0)
    return () => {
      window.clearTimeout(timer)
      databaseRef.current?.close()
    }
  }, [])

  const localVisit = data?.visits.find((visit) => visit.slug === MTC_SLUG)
  const localObservations = Array.isArray(localVisit?.observations) ? localVisit.observations : []
  const queuedMtcOperations = data?.operations.filter((operation) => operation.visitId === localVisit?.id) ?? []
  const queuedObservationOperations = queuedMtcOperations.filter((operation) => operation.recordType === 'observation')
  const localMedia = data?.media.filter((item) => !localVisit?.id || item.visitId === localVisit.id) ?? []
  const fallbackEntries = Object.entries(data?.localStorage ?? {})
    .filter(([key]) => key.startsWith('climatise-audit-cloud-fallback:'))
  const fallbackVisits = fallbackEntries.flatMap(([, value]) => {
    try {
      const parsed = JSON.parse(value) as { visit?: Record<string, unknown>; operations?: Array<Record<string, unknown>> }
      return parsed.visit ? [parsed] : []
    } catch {
      return []
    }
  })
  const fallbackMtc = fallbackVisits.find((entry) => entry.visit?.slug === MTC_SLUG)
  const fallbackObservations = Array.isArray(fallbackMtc?.visit?.observations) ? fallbackMtc.visit.observations : []

  const exportRecovery = async () => {
    if (!data) return
    setExporting(true)
    setError('')
    try {
      const zip = new JSZip()
      zip.file('recovery-report.json', JSON.stringify(withoutBinary(data), null, 2))
      data.visits.forEach((visit, index) => {
        zip.file(`visits/visit-${String(visit.slug ?? index)}.json`, JSON.stringify(visit, null, 2))
      })
      zip.file('queued-operations.json', JSON.stringify(data.operations, null, 2))
      zip.file('conflicts.json', JSON.stringify(data.conflicts, null, 2))
      zip.file('local-storage.json', JSON.stringify(data.localStorage, null, 2))

      const evidenceNames = new Map<string, string>()
      for (const visit of data.visits) {
        const evidence = Array.isArray(visit.evidence) ? visit.evidence as Array<Record<string, unknown>> : []
        evidence.forEach((item) => {
          if (typeof item.id === 'string' && typeof item.fileName === 'string') evidenceNames.set(item.id, item.fileName)
        })
      }
      data.media.forEach((item) => {
        const mediaBlob = item.blob ?? (item.data ? new Blob([item.data], { type: item.mimeType }) : null)
        if (!mediaBlob) return
        const originalName = evidenceNames.get(item.id) ?? `${item.id}.${(item.mimeType ?? 'application/octet-stream').split('/').at(-1)}`
        zip.file(`media/${originalName}`, mediaBlob)
      })

      download(await zip.generateAsync({ type: 'blob' }), `climatise-ipad-recovery-${new Date().toISOString().slice(0, 10)}.zip`)
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : 'The recovery package could not be created.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <main className="recovery-page">
      <section className="recovery-card">
        <span className="eyebrow">Emergency read-only recovery</span>
        <h1>Climatise iPad recovery</h1>
        <p>This screen does not open the audit, contact the sync API or change browser storage.</p>
        {status && <p className="alert alert--success" role="status">{status}</p>}
        {error && <p className="alert alert--danger" role="alert">{error}</p>}

        {data && (
          <>
            <div className="recovery-grid">
              <article><strong>{localObservations.length}</strong><span>MTC observations in IndexedDB</span></article>
              <article><strong>{queuedObservationOperations.length}</strong><span>queued observation edits</span></article>
              <article><strong>{localMedia.length}</strong><span>local media records</span></article>
              <article><strong>{fallbackObservations.length}</strong><span>MTC observations in cloud fallback</span></article>
            </div>
            <dl className="recovery-details">
              <div><dt>Local MTC visit found</dt><dd>{localVisit ? 'Yes' : 'No'}</dd></div>
              <div><dt>All queued MTC edits</dt><dd>{queuedMtcOperations.length}</dd></div>
              <div><dt>Conflicts retained</dt><dd>{data.conflicts.length}</dd></div>
              <div><dt>Database version</dt><dd>{data.databaseVersion ?? 'No existing database found'}</dd></div>
            </dl>
            <button className="button" type="button" onClick={() => void exportRecovery()} disabled={exporting}>
              {exporting ? 'Building recovery package…' : 'Download complete recovery package'}
            </button>
            <button className="button button--secondary" type="button" onClick={() => void scan()}>
              Scan again
            </button>
          </>
        )}
      </section>
    </main>
  )
}
